import {
  days,
  months,
  PATTERN_REGEX,
  PDF,
  USER_ROLES,
  WEB_SUBSCRIPTION_TYPES,
  WEBSITE,
  YOUTUBE,
} from "@root/webConstants";
import { USER_TYPES } from "@root/constants";
import { t } from "i18next";

export const chatObjSimplifier = (chatHistory: any[]) => {
  let history: any[] = [];
  let idCounter = 1;

  const filteredChat = chatHistory?.flatMap((chat, index) => {
    if (index === chatHistory.length - 1) {
      history = [...JSON.parse(chat?.answer)?.chat_history];
    }

    return [
      {
        role: "user",
        data: {
          queryRes: { answer: chat?.question },
          chatId: chat?.chatId,
          threadId: chat?.threadId,
          threadKuid: chat?.thread?.threadKuid,
          chatKuid: chat?.chatKuid,
        },
        id: idCounter++,
        childChats: chat?.childChats || [],
        chatIndex: 1,
        files: chat?.input_details ? JSON.parse(chat?.input_details) : {},
      },
      {
        role: "assistent",
        data: {
          queryRes: { ...JSON.parse(chat?.answer) },
          chatKuid: chat?.chatKuid,
          threadKuid: chat?.thread?.threadKuid,
          chatId: chat?.chatId,
          threadId: chat?.threadId,
        },
        id: idCounter++,
        childChats: chat?.childChats || [],
        chatIndex: 1,
        files: chat?.input_details ? JSON.parse(chat?.input_details) : {},
      },
    ];
  });
  return { chat: filteredChat, history };
};

export const calculateLastseen = (lastOnline: any) => {
  const currentTime: any = new Date();
  const lastOnlineTime: any = new Date(lastOnline);

  const timeDifference: number = Math.abs(currentTime - lastOnlineTime);
  const minutes = Math.floor(timeDifference / 60000);
  const hours = Math.floor(minutes / 60);
  if (minutes < 1) {
    return t("common:helper.few_seconds_ago");
  } else if (minutes < 60) {
    return `${minutes} ${
      minutes === 1 ? t("common:helper.minute") : t("common:helper.minutes")
    } ${t("common:helper.ago")}`;
  } else if (hours < 24) {
    return t("common:helper.hours_ago", { hour: hours });
  } else if (hours < 24 * 365) {
    const days = Math.floor(hours / 24);
    if (days > 29) {
      const months = Math.floor(days / 30);
      if (months === 1) {
        return t("common:helper.one_month_ago");
      } else {
        return t("common:helper.month_ago", { months: months });
      }
    } else {
      return `${days} ${
        days === 1 ? t("common:helper.day") : t("common:helper.days")
      } ${t("common:helper.ago")}`;
    }
  } else {
    const years = Math.floor(hours / (24 * 365));
    return `${years} ${
      years === 1 ? t("common:helper.year") : t("common:helper.years")
    } ${t("common:helper.ago")}`;
  }
};

export const isIndev = () => {
  if (process.env.REACT_APP_ENV === "development") {
    return true;
  } else {
    return false;
  }
};

export function calculateHasMore(currentChat: any[], maxChatSize: number) {
  return currentChat.length / 2 <= Number(maxChatSize - 1);
}

export const isRouteExist = (
  path: string,
  routes: string[],
  regex?: RegExp
) => {
  if (!regex) {
    return routes?.includes(path);
  } else {
    if (regex.test(path)) {
      return regex?.test(path);
    }
    return routes?.includes(path);
  }
};

export const isRoutePrefixMatch = (
  pathname: string,
  routePrefixes: string[]
) => {
  if (!routePrefixes || routePrefixes.length === 0) return false;
  return routePrefixes.some((prefix) => pathname.startsWith(prefix));
};

export const copyTextToClipboard = async (text: string, func?: any) => {
  await navigator.clipboard.writeText(text);
  if (func) {
    func();
  }
};
export function extractUniqueObjects(objects: any) {
  const seenFilenames = new Set();
  if (objects && objects?.length > 0) {
    return objects?.filter((obj: any) => {
      if (seenFilenames.has(obj.filename)) {
        return false;
      } else {
        seenFilenames.add(obj.filename);
        return true;
      }
    });
  } else {
    return [];
  }
}

export function removeExtraSlashes(url: string) {
  let parsedUrl = new URL(url);
  let normalizedPath = parsedUrl?.pathname
    ?.split("/")
    ?.filter(Boolean)
    ?.join("/");
  if (normalizedPath && !normalizedPath?.startsWith("/")) {
    normalizedPath = "/" + normalizedPath;
  }
  let normalizedUrl = `${parsedUrl.origin}${normalizedPath}${parsedUrl.search}${parsedUrl.hash}`;
  return normalizedUrl;
}

export function findAllIndexes(str: string, substring: string, offset = 0) {
  const indices = [];
  let startIndex = 0;

  while ((startIndex = str.indexOf(substring, startIndex)) > -1) {
    indices.push(startIndex + offset);
    startIndex += substring.length; // Move past the last found substring
  }

  return indices;
}

export function extractTaskString(str: string) {
  const parts = str.split("/");
  if (parts.length > 1) {
    return parts[1];
  }
  return "";
}

export const validateMonth = (month: number, year: number): boolean => {
  const currentYear = new Date().getFullYear() % 100;
  const currentMonth = new Date().getMonth() + 1;

  if (typeof month !== "number" || month < 1 || month > 12) {
    return false;
  }

  // Check if the year is the current year and the month is not in the past
  if (year === currentYear && month < currentMonth) {
    return false;
  }

  // If the year is greater than the current year, the month is automatically valid
  if (year > currentYear) {
    return true;
  }

  // If the year is less than the current year, the month is invalid
  if (year < currentYear) {
    return false;
  }

  return true;
};

export const validateYear = (year: number) => {
  if (!year || typeof year !== "number") {
    return false;
  }
  if (new Date().getFullYear() % 100 <= year && year?.toString()?.length <= 2) {
    return true;
  } else {
    return false;
  }
};

export const addQueryToUrl = (
  baseURL: string,
  additionalQueries: any,
  addingFromUrl = false
) => {
  let url = baseURL;
  let index = 0;

  for (let key in additionalQueries) {
    if (additionalQueries.hasOwnProperty(key)) {
      url += `${
        !addingFromUrl ? "&" : index === 0 ? "?" : "&"
      }${encodeURIComponent(key)}=${encodeURIComponent(
        additionalQueries[key]
      )}`;
      index++;
    }
  }

  return url;
};

export const usersManagementFormatDate = (dateString: any) => {
  const date = new Date(dateString);

  const formattedDate = date
    .toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    .replace(",", ""); // Remove the comma between date and time

  // Reformat the date to 'YYYY/MM/DD'
  const [month, day, year] = formattedDate.split(" ")[0].split("/");
  const time = formattedDate.split(" ")[1] + " " + formattedDate.split(" ")[2];

  return `${year}/${month}/${day} ${time}`;
};

export const userManagmenetMonthDate = (dateString: any) => {
  const date = new Date(dateString);
  if (isNaN(date?.getTime())) return "N/A";
  const month = String(date?.getMonth() + 1).padStart(2, "0");
  const day = String(date?.getDate()).padStart(2, "0");
  return `${month}-${day}`;
};

const getDayFromDate = (dateString: any) => {
  const date = new Date(dateString);
  if (date.getFullYear() === 1970) {
    return dateString;
  }
  const dayName = days[date?.getDay() || 0];
  return dayName;
};

const getMonth = (dateString: any) => {
  const date = new Date(dateString);
  const monthName = months[date?.getMonth() || 0];
  return monthName?.slice(0, 3);
};

const getDate = (dateString: any) => {
  const date = new Date(dateString);
  return date.getDate();
};

export function waveChartFormater(arr: any[]) {
  if (arr && arr.length > 0) {
    const date = new Date(arr?.[0]?.label);
    if (date.getFullYear() === 1970) {
      return arr.map((item) => ({
        x: getDayFromDate(item?.label),
        y: item?.Occurrences,
        tooltip_date: `${item?.label} ${months[new Date()?.getMonth()].slice(
          0,
          3
        )}`,
      }));
    }
    return arr
      .map((item) => ({
        x: getDayFromDate(item?.label),
        y: item?.Occurrences,
        tooltip_date: `${getDate(item?.label)} ${getMonth(item?.label)}`,
      }))
      ?.reverse();
  } else {
    return [{ x: 0, y: 0 }];
  }
}

export function lineChartFormater(arr: any[]) {
  if (arr && arr.length > 0) {
    const date = new Date(arr?.[0]?.label);
    if (date.getFullYear() === 1970) {
      return arr.map((item) => ({
        day: getDayFromDate(item?.label),
        users: item?.Occurrences,
      }));
    }
    return arr
      .map((item) => ({
        day: getDayFromDate(item?.label),
        users: item?.Occurrences,
      }))
      ?.reverse();
  } else {
    return [{ day: 0, users: 0 }];
  }
}

export function haveLength(arg: string): boolean {
  if (arg && arg?.length > 0) {
    return true;
  } else {
    return false;
  }
}

export const unixTimeToUTC = (date: string) => {
  if (date) {
    return new Date(Number(date) * 1000).toUTCString();
  } else {
    return "null";
  }
};

type AnyObject = {
  [key: string]: any;
};

export const querybuilder = (
  key: string,
  value: string,
  seprator?: string | number,
  inTrueFalse?: any
): AnyObject => {
  if (value === seprator) {
    return {};
  }
  if (inTrueFalse) {
    if (value === inTrueFalse) {
      return value && value.length > 0 ? { [key]: true } : {};
    } else {
      return value && value.length > 0 ? { [key]: false } : {};
    }
  }
  return value && value.length > 0 ? { [key]: value } : {};
};

export const getRoleByRoleId = (id: number) => {
  let role = null;
  if (id) {
    switch (id) {
      case 1:
        role = USER_ROLES.USER;
        break;

      case 2:
        role = USER_ROLES.ADMIN;
        break;

      case 3:
        role = USER_ROLES.ADMIN_USER;
        break;

      case USER_TYPES.ORGANIZATION_ADMIN:
        role = USER_ROLES.ADMIN;
        break;

      default:
        break;
    }
  }
  return role;
};

export const getIsAdminByRoleId = (id: number) => {
  if (id) {
    if (id === 1) {
      return false;
    }
    if (id === 2 || id === 3 || id === USER_TYPES.ORGANIZATION_ADMIN) {
      return true;
    }
  }
};

export const getSubsStatusAndColor = (
  status: "pending" | "inactive" | "active" | 1 | 2 | 3
): { bg: string; color: string; status: string } => {
  let data = {
    color: "theme.button.primary",
    bg: "theme.alert.200",
    status: WEB_SUBSCRIPTION_TYPES.ACTIVE,
  };
  if (!status) {
    return data;
  }
  switch (status) {
    case "active":
    case 3:
      data = {
        color: "theme.success.100",
        bg: "theme.success.50",
        status: WEB_SUBSCRIPTION_TYPES.ACTIVE,
      };
      break;
    case "pending":
    case 1:
      data = {
        color: "theme.yallow.100",
        bg: "theme.yallow.50",
        status: WEB_SUBSCRIPTION_TYPES.PENDING,
      };
      break;
    case "inactive":
    case 2:
      data = {
        color: "theme.danger.900",
        bg: "theme.danger.100",
        status: WEB_SUBSCRIPTION_TYPES.INACTIVE,
      };
      break;

    default:
      break;
  }
  return data;
};

export function capitalize(str: string) {
  if (!str) return ""; // Handle empty strings
  return str?.charAt(0)?.toUpperCase() + str?.slice(1);
}

export const requestStatusProvider = (requestStatus: 1 | 2 | 3) => {
  let status = "";
  switch (requestStatus) {
    case 1:
      status = t("common:allTransactions.pending");
      break;
    case 2:
      status = t("common:mainTable.rejected");
      break;
    case 3:
      status = t("common:mainTable.approved");
      break;

    default:
      break;
  }
  return status;
};

export const shouldShowPopover = (
  input: string,
  inputRef: React.RefObject<HTMLInputElement>,
  substring: string
) => {
  const selectionStart = inputRef?.current?.selectionStart || 0 + 5;
  const substringIndexes = findAllIndexes(input, substring, 5);

  return (
    (substringIndexes.includes(input.length) &&
      substringIndexes.includes(selectionStart)) ||
    substringIndexes.includes(selectionStart)
  );
};

export const isFuncType = (fn: any): fn is Function => {
  return typeof fn === "function";
};
export const isStringType = (str: string): str is string => {
  return typeof str === "string";
};

export const getFileDetails = (url: string) => {
  // Extract the base URL by removing the query parameters
  const baseUrl = url?.split(/\#|\?/)[0];

  // Extract the file extension
  const extension = baseUrl?.split(".").pop();

  // Return an object with the cleaned URL and the file extension
  return { url: baseUrl, ext: extension };
};

export const validateUrl = (selectedUrl: any, url: any) => {
  if (!selectedUrl) return true;

  // Check if the URL is a valid PDF URL
  if (selectedUrl === PDF) {
    if (PATTERN_REGEX["PDF"].test(url)) return true;
  } else if (selectedUrl === WEBSITE) {
    if (PATTERN_REGEX["WEBSITE"].test(url)) return true;
  } else if (selectedUrl === YOUTUBE) {
    if (PATTERN_REGEX["YOUTUBE"].test(url)) return true;
  } else return false;
};
